import { isRTL, translations, type Language, type Translations } from '@/i18n';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'urbanmaps-language';
const DEFAULT_LANGUAGE: Language = 'en';

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce((current, key) => {
        if (current && typeof current === 'object' && key in current) {
            return (current as Record<string, unknown>)[key];
        }
        return undefined;
    }, obj as unknown);
}

function getStoredLanguage(): Language {
    if (typeof window === 'undefined') return DEFAULT_LANGUAGE;

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored in translations) {
        return stored as Language;
    }
    return DEFAULT_LANGUAGE;
}

interface LanguageContextValue {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
    tArray: <T = unknown>(key: string) => T[];
    tObject: <T = unknown>(key: string) => T | undefined;
    isRTL: boolean;
    isInitialized: boolean;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

interface LanguageProviderProps {
    children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
    const [language, setLanguageState] = useState<Language>(DEFAULT_LANGUAGE);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const stored = getStoredLanguage();
        setLanguageState(stored);
        setIsInitialized(true);

        // Set HTML attributes
        document.documentElement.lang = stored;
        document.documentElement.dir = isRTL(stored) ? 'rtl' : 'ltr';
    }, []);

    const setLanguage = useCallback((lang: Language) => {
        setLanguageState(lang);
        localStorage.setItem(STORAGE_KEY, lang);

        // Update HTML attributes
        document.documentElement.lang = lang;
        document.documentElement.dir = isRTL(lang) ? 'rtl' : 'ltr';
    }, []);

    const t = useCallback((key: string): string => {
        const currentTranslations = translations[language] as unknown as Translations;
        const value = getNestedValue(currentTranslations as unknown as Record<string, unknown>, key);

        if (typeof value === 'string') {
            return value;
        }

        // Fallback to English if not found
        if (language !== 'en') {
            const fallbackValue = getNestedValue(translations.en as unknown as Record<string, unknown>, key);
            if (typeof fallbackValue === 'string') {
                return fallbackValue;
            }
        }

        // Return key if nothing found
        console.warn(`Translation missing for key: ${key}`);
        return key;
    }, [language]);

    // Get array or object translations
    const tArray = useCallback(<T = unknown,>(key: string): T[] => {
        const currentTranslations = translations[language] as unknown as Translations;
        const value = getNestedValue(currentTranslations as unknown as Record<string, unknown>, key);

        if (Array.isArray(value)) {
            return value as T[];
        }

        // Fallback to English
        if (language !== 'en') {
            const fallbackValue = getNestedValue(translations.en as unknown as Record<string, unknown>, key);
            if (Array.isArray(fallbackValue)) {
                return fallbackValue as T[];
            }
        }

        return [];
    }, [language]);

    const tObject = useCallback(<T = unknown,>(key: string): T | undefined => {
        const currentTranslations = translations[language] as unknown as Translations;
        const value = getNestedValue(currentTranslations as unknown as Record<string, unknown>, key);

        if (value && typeof value === 'object') {
            return value as T;
        }

        // Fallback to English
        if (language !== 'en') {
            const fallbackValue = getNestedValue(translations.en as unknown as Record<string, unknown>, key);
            if (fallbackValue && typeof fallbackValue === 'object') {
                return fallbackValue as T;
            }
        }

        return undefined;
    }, [language]);

    const value: LanguageContextValue = {
        language,
        setLanguage,
        t,
        tArray,
        tObject,
        isRTL: isRTL(language),
        isInitialized,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage(): LanguageContextValue {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}
